import {
  faArrowRight,
  faDatabase,
  faPaperPlane,
  faTrash,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { chain } from 'lodash';
import moment from 'moment';
import React, { useMemo, useState } from 'react';
import VoxelIconButton from '../../../components/voxel/Buttons/VoxelIconButton';
import VoxelChip from '../../../components/voxel/Chips/VoxelChip';
import VoxelTable from '../../../components/voxel/Tables/VoxelTable';
import VoxelTableCell from '../../../components/voxel/Tables/VoxelTableCell';
import VoxelTableHeadCell from '../../../components/voxel/Tables/VoxelTableHeadCell';
import VoxelTablePaginationDataSpecific from '../../../components/voxel/Tables/VoxelTablePaginationDataSpecific';
import VoxelTableRow from '../../../components/voxel/Tables/VoxelTableRow';
import VoxelTableRowEmpty from '../../../components/voxel/Tables/VoxelTableRowEmpty';
import VoxelAvatar from '../../../components/voxel/VoxelAvatar';
import VoxelTooltipShort from '../../../components/voxel/Tooltip/VoxelTooltipShort';
import { CurrentTeamGq } from '../../../graphql/users/models/CurrentTeamGq';
import { CurrentUserGq } from '../../../graphql/users/models/CurrentUserGq';
import { PagingInputGq } from '../../../graphql/users/models/PagingInputGq';
import { PagingResultGq } from '../../../graphql/users/models/PagingResultGq';
import { TeamConnectionGq } from '../../../graphql/users/models/TeamConnectionGq';
import DeleteConnectionDialog from './DeleteConnectionDialog';
import ResendConnectionInvitationDialog from './ResendConnectionInvitationDialog';

export interface TeamConnectionsTableProps {
  connections?: TeamConnectionGq[];
  team: CurrentTeamGq;
  user: CurrentUserGq;
  pagingInput: PagingInputGq;
  setPaging: React.Dispatch<React.SetStateAction<PagingInputGq>>;
  pagingResult?: PagingResultGq;
  reload: () => Promise<unknown>;
}

export const TeamConnectionsTable: React.FC<TeamConnectionsTableProps> = ({
  connections,
  pagingResult,
  setPaging,
  pagingInput,
  reload,
  team,
}) => {
  const [deleteDialogConnectionId, setDeleteDialogConnectionId] = useState<number>();
  const [resendInviteDialogId, setResendInviteDialogId] = useState<number>();

  const deleteConnection = connections.find((c) => c.id === deleteDialogConnectionId);
  const resendConnection = connections.find((c) => c.id === resendInviteDialogId);
  const spamCount = useMemo(
    () =>
      chain(connections)
        .flatMap((c) => c.invitations)
        .filter((c) => !!c.sentTime && moment.utc(c.sentTime).isAfter(moment.utc().add(-1, 'days')))
        .value().length,
    [connections]
  );
  const preventSpam = spamCount > 30;

  const handleSuccess = () => {
    return reload().then(() => {
      setDeleteDialogConnectionId(undefined);
      setResendInviteDialogId(undefined);
    });
  };

  if (!connections?.length) {
    return (
      <VoxelTable>
        <tbody>
          <VoxelTableRowEmpty colSpan={4}>No team connections found</VoxelTableRowEmpty>
        </tbody>
      </VoxelTable>
    );
  }

  return (
    <>
      <DeleteConnectionDialog
        connection={deleteConnection}
        onSuccess={handleSuccess}
        close={() => setDeleteDialogConnectionId(undefined)}
      />
      <ResendConnectionInvitationDialog
        connection={resendConnection}
        onSuccess={reload}
        close={() => setResendInviteDialogId(undefined)}
        preventSpam={preventSpam}
      />
      <VoxelTable>
        <thead>
          <VoxelTableRow>
            <VoxelTableHeadCell align="left">Connection</VoxelTableHeadCell>
            <VoxelTableHeadCell align="center">Created by</VoxelTableHeadCell>
            <VoxelTableHeadCell align="right" />
          </VoxelTableRow>
        </thead>
        <tbody>
          {connections?.map((connection, index) => {
            // Connection tooltip
            let connectionTooltip = '';
            if (!!connection.sharingTeam && !!connection.receivingTeam) {
              connectionTooltip = `${connection.sharingTeam.name} shares all of its project data with ${connection.receivingTeam.name}. ${connection.receivingTeam.name} receives a sponsored subscription plan if the team doesn't already have a plan.`;
            } else if (!!connection.receivingTeam) {
              connectionTooltip = `An unanswered request to read another team's project data`;
            } else if (!!connection.sharingTeam) {
              connectionTooltip = `An unanswered invitation to share your team's project data`;
            }

            let connectedByTooltip = '';
            let connectedBy: string[] = [];
            if (connection.connectedBySharingTeamUser?.email?.length)
              connectedBy.push(connection.connectedBySharingTeamUser?.email);
            if (connection.connectedByReceivingTeamUser?.email?.length)
              connectedBy.push(connection.connectedByReceivingTeamUser?.email);
            let connectedByUsers = connectedBy.length ? `by ${connectedBy.join(' and ')}` : '';

            if (!!connection.sharingTeam && !!connection.receivingTeam) {
              connectedByTooltip = `Connected ${moment
                .utc(connection.createTime)
                .format('MMM Do, yyyy ')} ${connectedByUsers}`;
            } else {
              connectedByTooltip = `Invited ${moment
                .utc(connection.createTime)
                .format('MMM Do, yyyy ')} ${connectedByUsers}`;
            }

            const expired =
              !(connection.receivingTeam && connection.sharingTeam) &&
              connection.invitations.every((i) => moment.utc(i.tokenExpirationTime).isBefore());

            return (
              <VoxelTableRow key={connection.id}>
                <VoxelTableCell align="left">
                  <div className="flex">
                    {' '}
                    {/* An extra flex wrapper so that the tooltip is centered on the content (it forces the tooltip to not take full width) */}
                    <VoxelTooltipShort message={connectionTooltip}>
                      <div className="flex flex-row items-center gap-4 flex-nowrap">
                        <VoxelChip title={connection.sharingTeam?.name ?? '?'} />

                        <div className="flex flex-row items-center gap-2 flex-nowrap">
                          <FontAwesomeIcon
                            icon={faDatabase}
                            size="lg"
                            className="text-voxel-gray-700"
                          />
                          <FontAwesomeIcon icon={faArrowRight} className="text-voxel-gray-700" />
                        </div>
                        <VoxelChip title={connection.receivingTeam?.name ?? '?'} />
                      </div>
                    </VoxelTooltipShort>
                  </div>
                </VoxelTableCell>
                <VoxelTableCell align="center">
                  <VoxelTooltipShort message={connectedByTooltip}>
                    <div className="flex flex-row items-center justify-center gap-2">
                      <div className='flex flex-row flex-nowrap items-center gap-2'>
                        {!!connection.connectedBySharingTeamUser?.email?.length && (
                          <VoxelAvatar
                            size="sm"
                            name={connection.connectedBySharingTeamUser.email}
                            img={connection.connectedBySharingTeamUser.picture}
                            tooltip={
                              <>
                                {connection.connectedBySharingTeamUser?.givenName + ' ' + connection.connectedBySharingTeamUser?.familyName}
                                <br />
                                {connection.connectedBySharingTeamUser?.email}
                              </>
                            }
                          />
                        )}
                        {!!connection.connectedByReceivingTeamUser?.email?.length && (
                          <VoxelAvatar
                            size="sm"
                            name={connection.connectedByReceivingTeamUser.email}
                            img={connection.connectedByReceivingTeamUser.picture}
                            tooltip={
                              <>
                                {connection.connectedByReceivingTeamUser?.givenName + ' ' + connection.connectedByReceivingTeamUser?.familyName}
                                <br />
                                {connection.connectedByReceivingTeamUser?.email}
                              </>
                            }
                          />
                        )}
                      </div>
                      <div>{moment.utc(connection.createTime).format('MMM do, YY')}</div>
                    </div>
                  </VoxelTooltipShort>
                </VoxelTableCell>
                <VoxelTableCell
                  align="right"
                  className="flex flex-row justify-end gap-1 items-center flex-nowrap"
                >
                  {expired && (
                    <VoxelChip
                      tooltip="The invitation doesn't work anymore"
                      title="expired invitation"
                      color="warning"
                    />
                  )}
                  {(!connection.receivingTeam || !connection.sharingTeam) && (
                    <VoxelIconButton
                      icon={<FontAwesomeIcon icon={faPaperPlane} size="lg" />}
                      disabled={!team.accessUserCanEditTeamConnections}
                      tooltip="Resend invitation"
                      onClick={() => setResendInviteDialogId(connection.id)}
                      color="transparent"
                    />
                  )}
                  <VoxelIconButton
                    icon={<FontAwesomeIcon icon={faTrash} size="lg" />}
                    tooltip="Remove"
                    disabled={!team.accessUserCanEditTeamConnections}
                    onClick={() => setDeleteDialogConnectionId(connection.id)}
                    color="transparent"
                  />
                </VoxelTableCell>
              </VoxelTableRow>
            );
          })}
          <VoxelTablePaginationDataSpecific
            colSpan={7}
            pagingInput={pagingInput}
            pagingResult={pagingResult}
            onPagingChange={(event, paging) => setPaging(paging)}
          />
        </tbody>
      </VoxelTable>
    </>
  );
};

export default TeamConnectionsTable;
